// Who is actually in the active room — a centered header above the message thread,
// mirroring iMessage's and WhatsApp's own contact-name bar. Two earlier shapes were tried
// and rejected first (see git history): a plain label above the scroller read as stray
// text rather than a heading even styled like a section title, and moving it into the
// plan-name row read as disconnected from the thread it was describing. A round of
// mockups settled on this one instead — scoped to the thread column, not the plan header,
// so it stays visually attached to the messages it describes.
//
// Never recites the plan's whole roster: a group thread's membership already equals
// "everyone", so "Everyone and Mor" says the whole thing without listing anyone, and a
// roster with a duplicate or a misnamed entry never gets a chance to recite its own
// mistakes here. The principals' room is the one case that names somebody — read off
// the thread's own participants.
import type { Person, Thread } from '../api/contract.js';
import { principalsRoomLabel } from '../format.js';

export type ThreadRosterProps = {
  people: readonly Person[];
  /** This room's own thread; its participants name the principals' room. */
  thread?: Thread | null;
  /** `'shared'` is the one group conversation; `'principals'` is the co-principals' room,
   * named when it safely can be; anything else is the reader's own 1:1. Same wording as the
   * matching room-switcher entry, so the heading and the switcher agree. */
  kind: 'shared' | 'principals' | 'private';
  /** True when the room switcher is showing: its selected tab already carries this name
   * (and the "Private" marker), so the heading is not drawn a second time. */
  switcherShown?: boolean;
};

export function ThreadRoster({
  people,
  kind,
  thread = null,
  switcherShown = false,
}: ThreadRosterProps): JSX.Element | null {
  if (switcherShown) {
    return null;
  }
  const text =
    kind === 'shared'
      ? 'Everyone and Mor'
      : kind === 'principals'
        ? `You, ${principalsRoomLabel(thread, people)} and Mor`
        : 'You and Mor';
  return (
    <div className="threadHead">
      <p className="threadHead__label threadHead__who">{text}</p>
      {kind === 'private' ? <p className="threadHead__label">Private</p> : null}
    </div>
  );
}
